import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';

// Configure axios to use the base URL from environment variables
axios.defaults.baseURL = process.env.REACT_APP_API_BASE_URL || '';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('token') || null);

  useEffect(() => {
    // Check if user is logged in on component mount
    const checkLoginStatus = async () => {
      try {
        if (!token) {
          console.log('No token found, user is not authenticated');
          setLoading(false);
          return;
        }
        
        // Set default auth header for all requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        try {
          // Verify token by fetching user data - removed /api prefix
          const response = await axios.get('/auth/me');
          
          if (response.data && response.data.success) {
            console.log('Token verified, user is authenticated');
            setCurrentUser(response.data.user);
            setIsAuthenticated(true);
          } else {
            console.log('Token verification failed: Invalid response');
            handleLogout();
          }
        } catch (err) {
          console.error('Error verifying token:', err);
          
          // If there's a server error, try using validate-token endpoint instead
          try {
            const validateResponse = await axios.get('/auth/validate-token');
            if (validateResponse.data && validateResponse.data.success) {
              console.log('Token validated using fallback endpoint');
              setCurrentUser(validateResponse.data.user);
              setIsAuthenticated(true);
            } else {
              console.log('Fallback token validation failed');
              handleLogout();
            }
          } catch (fallbackErr) {
            console.error('Fallback validation also failed:', fallbackErr);
            handleLogout();
          }
        }
      } catch (error) {
        console.error('Error in authentication check:', error);
        handleLogout();
      } finally {
        setLoading(false);
      }
    };
    
    checkLoginStatus();
  }, [token]);
  
  const handleLogout = () => {
    console.log('Logging out user due to authentication failure');
    localStorage.removeItem('token');
    setToken(null);
    delete axios.defaults.headers.common['Authorization'];
    setCurrentUser(null);
    setIsAuthenticated(false);
  };
  
  // Login function
  const login = async (email, password) => {
    try {
      setError(null);
      // Updated to remove /api prefix
      const response = await axios.post('/auth/login', { email, password });
      const { token: newToken, user } = response.data;
      
      if (newToken) {
        console.log('Login successful, setting token');
        localStorage.setItem('token', newToken);
        setToken(newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        setCurrentUser(user);
        setIsAuthenticated(true);
        return true;
      } else {
        throw new Error('No token received from server');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
      return false;
    }
  };
  
  // Logout function
  const logout = () => {
    handleLogout();
  };
  
  // Get auth header
  const getAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
  
  const value = {
    currentUser,
    user: currentUser, // Add this to make it compatible with ProtectedRoute
    loading,
    login,
    logout,
    getAuthHeader,
    isAuthenticated,
    error,
    token
  };
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
